import { skuAttributesFields } from "@nexxt/contracts/catalog";
import { type AttributeField, missingAttributes } from "@nexxt/contracts/catalog/rules";
import { type CellOption, type DraftState, parseDecimal, plainDecimal } from "@nexxt/ui";
import type * as z from "zod";
import type { AttributeOptions, SkuAttributes } from "./attributes.queries";

// The grid of the attributes of a task, as data (pattern.md §10): its columns and their groups, what a cell shows and
// offers, the values typed and not saved yet, and what a save sends. No React here: the screen renders it.

const fields = skuAttributesFields.shape;
export type AttributeKey = keyof typeof fields;

/** How a cell is edited: typed, typed as a number, picked from reference data, or yes / no. */
type AttributeKind = "text" | "decimal" | "choice" | "flag";

const groups = ["customs", "descriptions", "composition", "animal", "weights"] as const;
type AttributeGroup = (typeof groups)[number];

export interface AttributeColumn {
  field: AttributeKey;
  kind: AttributeKind;
  group: AttributeGroup;
  /** Decimals a number may have. */
  scale?: number;
}

/** Every attribute an SKU has, in the order of the grid. */
const columns: AttributeColumn[] = [
  { field: "ean", kind: "text", group: "customs" },
  { field: "customsClassification", kind: "choice", group: "customs" },
  { field: "hsCode", kind: "text", group: "customs" },
  { field: "commodityCode", kind: "text", group: "customs" },
  { field: "countryWhenceConsigned", kind: "choice", group: "customs" },
  { field: "manufacturerId", kind: "choice", group: "descriptions" },
  { field: "supplierStyleCode", kind: "text", group: "descriptions" },
  { field: "supplierCode", kind: "text", group: "descriptions" },
  { field: "purchaseDescription", kind: "text", group: "descriptions" },
  { field: "tomorrowColourDescription", kind: "text", group: "descriptions" },
  { field: "matrixColourDescription", kind: "text", group: "descriptions" },
  { field: "composition", kind: "text", group: "composition" },
  { field: "compositionExtras", kind: "text", group: "composition" },
  { field: "washingCareInstructions", kind: "text", group: "composition" },
  { field: "animalScientificName", kind: "text", group: "animal" },
  { field: "animalMaterialPct", kind: "decimal", group: "animal", scale: 2 },
  { field: "animalCountryOfOrigin", kind: "choice", group: "animal" },
  { field: "farmRaised", kind: "flag", group: "animal" },
  { field: "dyed", kind: "flag", group: "animal" },
  { field: "weightGross", kind: "decimal", group: "weights", scale: 3 },
  { field: "weightNet", kind: "decimal", group: "weights", scale: 3 },
  { field: "weightUnit", kind: "choice", group: "weights" },
];

/** The columns on display: the attributes every SKU needs, one group, or all of them. */
export const columnSets = ["required", ...groups, "all"] as const;
export type ColumnSet = (typeof columnSets)[number];

type Sku = Pick<SkuAttributes, AttributeKey | "skuId" | "version" | "missing" | "productCategory">;

const facts = (sku: Partial<Sku>) => ({
  productCategory: sku.productCategory ?? null,
  ean: sku.ean ?? null,
  hsCode: sku.hsCode ?? null,
  commodityCode: sku.commodityCode ?? null,
  manufacturerId: sku.manufacturerId ?? null,
  compositionExtras: sku.compositionExtras ?? null,
  washingCareInstructions: sku.washingCareInstructions ?? null,
  weightGross: sku.weightGross ?? null,
  weightNet: sku.weightNet ?? null,
  weightUnit: sku.weightUnit ?? null,
  countryWhenceConsigned: sku.countryWhenceConsigned ?? null,
  animalScientificName: sku.animalScientificName ?? null,
  animalMaterialPct: sku.animalMaterialPct ?? null,
  animalCountryOfOrigin: sku.animalCountryOfOrigin ?? null,
  farmRaised: sku.farmRaised ?? null,
  dyed: sku.dyed ?? null,
});

/** What the domain's rule asks of an SKU that declares nothing: the only list of the required attributes is there. */
const requiredOnEverySku: readonly string[] = missingAttributes(facts({}));

export function columnsOf(set: ColumnSet): AttributeColumn[] {
  if (set === "all") return columns;
  if (set === "required") return columns.filter((c) => requiredOnEverySku.includes(c.field));
  return columns.filter((c) => c.group === set);
}

const columnOf = (field: AttributeKey) => columns.find((c) => c.field === field) as AttributeColumn;

/** The saved value of a cell as its editor takes it: text as it is, a number as typed, a choice by its value. */
export function cellText(sku: Sku, field: AttributeKey): string {
  const value = sku[field];
  if (value === null || value === undefined) return "";
  return columnOf(field).kind === "decimal" ? plainDecimal(String(value)) : String(value);
}

interface ChoiceLabels {
  none: string;
  yes: string;
  no: string;
}

const coded = (items: readonly { code: string; name: string }[]): CellOption[] =>
  items.map(({ code, name }) => ({ value: code, label: code === name ? name : `${code} · ${name}` }));

/**
 * What a cell is chosen from, the empty choice first; undefined for a cell that is typed. Reference data by code,
 * the manufacturer by id.
 */
export function cellOptions(
  field: AttributeKey,
  options: AttributeOptions | undefined,
  labels: ChoiceLabels,
): CellOption[] | undefined {
  const { kind } = columnOf(field);
  if (kind !== "choice" && kind !== "flag") return undefined;
  const none = { value: "", label: labels.none };
  if (kind === "flag") return [none, { value: "true", label: labels.yes }, { value: "false", label: labels.no }];
  const values: Partial<Record<AttributeKey, CellOption[]>> = {
    customsClassification: coded(options?.customsClassifications ?? []),
    countryWhenceConsigned: coded(options?.countries ?? []),
    animalCountryOfOrigin: coded(options?.countries ?? []),
    weightUnit: coded(options?.weightUnits ?? []),
    manufacturerId: (options?.manufacturers ?? []).map(({ id, name }) => ({ value: id, label: name })),
  };
  return [none, ...(values[field] ?? [])];
}

/**
 * A value as its cell shows it: the label of a choice, the text otherwise. A manufacturer the user cannot pick is
 * still shown by its name.
 */
export function cellDisplay(
  sku: Pick<SkuAttributes, "manufacturerId" | "manufacturer">,
  field: AttributeKey,
  value: string,
  options: readonly CellOption[] | undefined,
): string {
  if (value === "" || !options) return value;
  const label = options.find((o) => o.value === value)?.label;
  return label ?? (field === "manufacturerId" && value === sku.manufacturerId ? (sku.manufacturer ?? value) : value);
}

/** The text of a cell as the API takes it, or `invalid` when the format of the attribute refuses it. */
type Parsed = { value: string | boolean | null } | "invalid";

function parseCell(field: AttributeKey, text: string): Parsed {
  const { kind, scale } = columnOf(field);
  const typed = text.trim();
  if (typed === "") return { value: null };
  if (kind === "flag") return { value: typed === "true" };
  const value = kind === "decimal" ? parseDecimal(typed, scale) : typed;
  if (value === null) return "invalid";
  // the formats are the contract's: the same schema the gateway validates the save with
  const checked = fields[field].safeParse(value);
  return checked.success ? { value: checked.data ?? null } : "invalid";
}

const sameValue = (field: AttributeKey, a: string | boolean | null, b: string | boolean | null): boolean =>
  columnOf(field).kind === "decimal" && a !== null && b !== null ? Number(a) === Number(b) : a === b;

/** Attributes of an SKU typed in the grid and not saved yet: the text as typed, on the version it was typed on. */
interface AttributeDraft {
  skuId: string;
  version: number;
  values: Partial<Record<AttributeKey, string>>;
}

/** By SKU: the screen keeps them until one save sends them all. */
export type AttributeDrafts = ReadonlyMap<string, AttributeDraft>;

/**
 * The drafts after a cell was left with `text`. The value the cell already has is no change: the draft of that field
 * goes away. Anything else stays as typed, a text the attribute does not take too: the cell says so.
 */
export function setDraft(drafts: AttributeDrafts, sku: Sku, field: AttributeKey, text: string): AttributeDrafts {
  const parsed = parseCell(field, text);
  const unchanged = parsed !== "invalid" && sameValue(field, parsed.value, sku[field] ?? null);
  const { [field]: _dropped, ...rest } = drafts.get(sku.skuId)?.values ?? {};
  const values = unchanged ? rest : { ...rest, [field]: text.trim() };
  const next = new Map(drafts);
  if (Object.keys(values).length > 0) next.set(sku.skuId, { skuId: sku.skuId, version: sku.version, values });
  else next.delete(sku.skuId);
  return next;
}

/** Whether a drafted cell can be saved; undefined for a cell without a draft. */
export function draftState(
  draft: AttributeDraft | undefined,
  field: AttributeKey,
  sku: Pick<Sku, "version"> | undefined,
): DraftState | undefined {
  const typed = draft?.values[field];
  if (draft === undefined || typed === undefined) return undefined;
  if (parseCell(field, typed) === "invalid") return "invalid";
  return sku && sku.version !== draft.version ? "stale" : "ok";
}

/** How many cells are drafted, on how many SKUs, and how many of them stop the save. */
export function draftSummary(drafts: AttributeDrafts, skus: readonly Pick<Sku, "skuId" | "version">[]) {
  const summary = { changes: 0, skus: drafts.size, invalid: 0, stale: 0 };
  for (const draft of drafts.values()) {
    // an SKU the filters no longer show is checked by the save itself
    const sku = skus.find((s) => s.skuId === draft.skuId);
    for (const field of Object.keys(draft.values) as AttributeKey[]) {
      const state = draftState(draft, field, sku);
      if (!state) continue;
      summary.changes += 1;
      if (state !== "ok") summary[state] += 1;
    }
  }
  return summary;
}

/** The values of a draft as the API takes them; a cell that is not valid is left out. */
function draftValues(draft: AttributeDraft): z.input<typeof skuAttributesFields> {
  const values: Partial<Record<AttributeKey, string | boolean | null>> = {};
  for (const [field, text] of Object.entries(draft.values) as [AttributeKey, string][]) {
    const parsed = parseCell(field, text);
    if (parsed !== "invalid") values[field] = parsed.value;
  }
  // each value passed the schema of its own field
  return values as z.input<typeof skuAttributesFields>;
}

/** The drafts as the rows of one save: each SKU with the version read and only the attributes changed. */
export function savePayload(drafts: AttributeDrafts) {
  return [...drafts.values()].map((draft) => ({ skuId: draft.skuId, version: draft.version, ...draftValues(draft) }));
}

/**
 * The rows that put back a save of `drafts` (ADR-282): each SKU with the values it had in `before`, on the version the
 * save answered. Null when an SKU of the save is not in `before` (the filters no longer show it): its values are not
 * known, so nothing is offered.
 */
export function undoPayload(
  drafts: AttributeDrafts,
  before: readonly Sku[],
  saved: readonly Pick<Sku, "skuId" | "version">[],
): ReturnType<typeof savePayload> | null {
  const was = new Map(before.map((sku) => [sku.skuId, sku]));
  const versions = new Map(saved.map((sku) => [sku.skuId, sku.version]));
  const rows: ReturnType<typeof savePayload> = [];
  for (const draft of drafts.values()) {
    const sku = was.get(draft.skuId);
    const version = versions.get(draft.skuId);
    if (!sku || version === undefined) return null;
    const values = Object.fromEntries(
      Object.keys(draftValues(draft)).map((field) => [field, sku[field as AttributeKey] ?? null]),
    );
    rows.push({ skuId: draft.skuId, version, ...(values as z.input<typeof skuAttributesFields>) });
  }
  return rows;
}

/**
 * The required attributes an SKU still misses, counting what is typed and not saved: the domain's own rule on the
 * drafted values. An attribute hidden from the user is not reported unless the domain did.
 */
export function missingWith(sku: Sku, draft: AttributeDraft | undefined): string[] {
  if (!draft) return sku.missing;
  const drafted = { ...sku, ...draftValues(draft) } as Partial<Sku>;
  return missingAttributes(facts(drafted)).filter(
    (field: AttributeField) => sku[field] !== undefined || sku.missing.includes(field),
  );
}
